import { useState } from 'react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Trash2, Image, Video, Layers, CheckCircle, XCircle, Clock } from 'lucide-react';
import { api } from '../../App';
import { toast } from 'sonner';

const VIDEO_STATUS_BADGE = {
  pending: { label: 'Pending', className: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approved', className: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Rejected', className: 'bg-red-100 text-red-700', icon: XCircle },
};

export function AdminContentTab({ allContent, contentFilter, setContentFilter, onDeleteContent, onRefresh }) {
  const [updatingId, setUpdatingId] = useState(null);

  const handleVideoStatus = async (videoId, status) => {
    setUpdatingId(videoId);
    try {
      await api.put(`/admin/videos/${videoId}/status`, { status });
      toast.success(`Video ${status}`);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <>
      <div className="flex items-center gap-3 mb-4">
        <Select value={contentFilter} onValueChange={setContentFilter}>
          <SelectTrigger className="w-48 input-zuriya"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Content</SelectItem>
            <SelectItem value="image">Portfolio Images</SelectItem>
            <SelectItem value="video">Videos</SelectItem>
            <SelectItem value="transformation">Transformations</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-stone-400">{allContent.length} items</span>
      </div>
      {allContent.length === 0 ? (
        <div className="card-surface p-12 text-center"><p className="text-stone-400">No content uploaded yet</p></div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {allContent.map(c => {
            const isVideo = c.type === 'video';
            const badge = isVideo ? (VIDEO_STATUS_BADGE[c.status] || VIDEO_STATUS_BADGE.pending) : null;
            const BadgeIcon = badge?.icon;
            return (
              <div key={`${c.type}-${c.id}`} className="group relative card-surface overflow-hidden" data-testid={`content-${c.type}-${c.id}`}>
                <div className="aspect-square bg-black relative">
                  {isVideo ? (
                    <video src={c.url} className="w-full h-full object-cover" muted preload="metadata" />
                  ) : (
                    <img src={c.url} alt="" className="w-full h-full object-cover" loading="lazy" />
                  )}
                  {/* Delete overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <Button size="sm" variant="destructive" onClick={() => onDeleteContent(c.type, c.id)} className="h-8 px-3 bg-red-500 text-white">
                      <Trash2 className="h-3.5 w-3.5 mr-1" />Remove
                    </Button>
                  </div>
                </div>
                <div className="p-2 space-y-1.5">
                  <div className="flex items-center gap-1">
                    {c.type === 'image' && <Image className="h-3 w-3 text-stone-400" />}
                    {isVideo && <Video className="h-3 w-3 text-stone-400" />}
                    {c.type === 'transformation' && <Layers className="h-3 w-3 text-[#D4AF37]" />}
                    <span className="text-xs text-stone-500 truncate">{c.provider_name || c.brand_name || ','}</span>
                  </div>
                  {/* Video approval controls */}
                  {isVideo && (
                    <div>
                      <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${badge.className}`}>
                        <BadgeIcon className="h-2.5 w-2.5" />
                        {badge.label}
                      </div>
                      {c.status !== 'approved' && (
                        <div className="flex gap-1 mt-1">
                          <button
                            onClick={() => handleVideoStatus(c.id, 'approved')}
                            disabled={updatingId === c.id}
                            className="flex-1 text-[10px] py-1 rounded bg-green-50 text-green-700 hover:bg-green-100 transition-colors font-medium"
                            data-testid={`approve-video-${c.id}`}
                          >
                            Approve
                          </button>
                          {c.status !== 'rejected' && (
                            <button
                              onClick={() => handleVideoStatus(c.id, 'rejected')}
                              disabled={updatingId === c.id}
                              className="flex-1 text-[10px] py-1 rounded bg-red-50 text-red-700 hover:bg-red-100 transition-colors font-medium"
                              data-testid={`reject-video-${c.id}`}
                            >
                              Reject
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
